/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  POST_SD_GET_EXTERNAL_USER_SETTINGS,
  POST_SD_SET_EXTERNAL_USER_SETTINGS,
  GET_CODETABLES_BY_TABLE_NAME,
  GET_SD_USER_EMAIL_LINKED_TO_OTHER_ACCOUNT_BY_EMAIL_ADDRESS,
  POST_SD_SEND_VERIFICATION_CODE,
  POST_SD_VERIFY_SUBMITTED_CODE,
} from './actionTypes';

import {
  postSdGetExternalUserSettingsUtil,
  postSdSetExternalUserSettingsUtil,
  getCodetablesByTableNameUtil,
  getSdUserEmailLinkedToOtherAccountByEmailAddressUtil,
  postSdSendVerificationCodeUtil,
  postSdVerifySubmittedCodeUtil,
} from './utils';

/**
 * Contains the action creator functions that update the SDYourSettings slice of the store.
 *
 * @namespace SDYourSettingsActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_get_external_user_settings
   *
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof SDYourSettingsActions
   */
  static postSdGetExternalUserSettings = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postSdGetExternalUserSettingsUtil.bind(null, {}),
      POST_SD_GET_EXTERNAL_USER_SETTINGS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postSdGetExternalUserSettings.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdGetExternalUserSettings = (dispatch, data) => {
    const json = {
      type: POST_SD_GET_EXTERNAL_USER_SETTINGS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postSdGetExternalUserSettings.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdGetExternalUserSettingsError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_SD_GET_EXTERNAL_USER_SETTINGS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_set_external_user_settings
   *
   * @param {NewSDExternalUserSettingsDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof SDYourSettingsActions
   */
  static postSdSetExternalUserSettings = (dispatch, newSDExternalUserSettingsDetails, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postSdSetExternalUserSettingsUtil.bind(null, newSDExternalUserSettingsDetails),
      POST_SD_SET_EXTERNAL_USER_SETTINGS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postSdSetExternalUserSettings.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdSetExternalUserSettings = (dispatch, data) => {
    const json = {
      type: POST_SD_SET_EXTERNAL_USER_SETTINGS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postSdSetExternalUserSettings.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdSetExternalUserSettingsError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_SD_SET_EXTERNAL_USER_SETTINGS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/codetables/{table_name}
   *
   * @param {string} table_name.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDYourSettingsActions
   */
  static getCodetablesByTableName = (dispatch, table_name, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getCodetablesByTableNameUtil.bind(null, table_name),

      GET_CODETABLES_BY_TABLE_NAME,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getCodetablesByTableName.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetGetCodetablesByTableName = (dispatch, data) => {
    const json = {
      type: GET_CODETABLES_BY_TABLE_NAME.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getCodetablesByTableName.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetGetCodetablesByTableNameError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_CODETABLES_BY_TABLE_NAME.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_user_email_linked_to_other_account/{emailAddress}
   *
   * @param {string} emailAddress.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDYourSettingsActions
   */
  static getSdUserEmailLinkedToOtherAccountByEmailAddress = (dispatch, emailAddress, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getSdUserEmailLinkedToOtherAccountByEmailAddressUtil.bind(null, emailAddress),

      GET_SD_USER_EMAIL_LINKED_TO_OTHER_ACCOUNT_BY_EMAIL_ADDRESS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getSdUserEmailLinkedToOtherAccountByEmailAddress.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetGetSdUserEmailLinkedToOtherAccountByEmailAddress = (dispatch, data) => {
    const json = {
      type: GET_SD_USER_EMAIL_LINKED_TO_OTHER_ACCOUNT_BY_EMAIL_ADDRESS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getSdUserEmailLinkedToOtherAccountByEmailAddress.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetGetSdUserEmailLinkedToOtherAccountByEmailAddressError = dispatch => {
    ReduxUtils.resetErrorAction(
      dispatch,
      GET_SD_USER_EMAIL_LINKED_TO_OTHER_ACCOUNT_BY_EMAIL_ADDRESS.failureAction
    );
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_send_verification_code
   *
   * @param {NewSDExternalUserVerificationMode} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof SDYourSettingsActions
   */
  static postSdSendVerificationCode = (dispatch, newSDExternalUserVerificationMode, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postSdSendVerificationCodeUtil.bind(null, newSDExternalUserVerificationMode),
      POST_SD_SEND_VERIFICATION_CODE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postSdSendVerificationCode.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdSendVerificationCode = (dispatch, data) => {
    const json = {
      type: POST_SD_SEND_VERIFICATION_CODE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postSdSendVerificationCode.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdSendVerificationCodeError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_SD_SEND_VERIFICATION_CODE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_verify_submitted_code
   *
   * @param {NewSDExternalUserVerificationMode} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof SDYourSettingsActions
   */
  static postSdVerifySubmittedCode = (dispatch, newSDExternalUserVerificationMode, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postSdVerifySubmittedCodeUtil.bind(null, newSDExternalUserVerificationMode),
      POST_SD_VERIFY_SUBMITTED_CODE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postSdVerifySubmittedCode.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdVerifySubmittedCode = (dispatch, data) => {
    const json = {
      type: POST_SD_VERIFY_SUBMITTED_CODE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postSdVerifySubmittedCode.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDYourSettingsActions
   */
  static resetPostSdVerifySubmittedCodeError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_SD_VERIFY_SUBMITTED_CODE.failureAction);
  };
}
